import {
    type UseToastOptions as BaseInterface,
    Box,
    type ToastId,
    useToast as useBaseToast,
    useTheme,
} from '@chakra-ui/react'
import type React from 'react'
import { type ReactNode, useMemo } from 'react'
import { Alert, type AlertProps } from '../../components/Alert'

type ToastProps = {
    content: ReactNode
    /**
     * @default "default"
     */
    status?: AlertProps['variant']
    cta?: AlertProps['CTA']
    /**
     * The delay before the toast hides (in milliseconds)
     * If set to `null`, toast will never dismiss.
     *
     * @default 5000
     */
    duration?: BaseInterface['duration']
    /**
     * @default "bottom-right"
     */
    position?: BaseInterface['position']
    /**
     * @default false
     */
    withAlert?: boolean
    /**
     * @default true
     */
    isClosable?: boolean
}

type ToastFunction = (props: ToastProps) => ToastId

type UseToastReturn = ToastFunction & {
    close: (toastId: ToastId) => void
    closeAll: () => void
}

export function useToast(): UseToastReturn {
    const { direction } = useTheme()
    const rtl = direction === 'rtl'

    const baseToast = useBaseToast({
        isClosable: true,
        containerStyle: { margin: 0, minWidth: 'unset' },
    })
    const toast = useMemo(
        () =>
            Object.assign(
                ({
                    content,
                    cta,
                    duration = 5000,
                    position = rtl ? 'bottom-left' : 'bottom-right',
                    status = 'default',
                    withAlert = true,
                    isClosable = true,
                }: ToastProps) => {
                    return baseToast({
                        isClosable,
                        duration,
                        position,
                        render: ({ id, onClose }) => (
                            <Box
                                id={id?.toString()}
                                mb={4}
                                px={6}
                                minW={['100vw', 94]}
                            >
                                {withAlert ? (
                                    <Alert
                                        variant={status}
                                        CTA={cta}
                                        m={0}
                                        isClosable={isClosable}
                                        onClose={onClose}
                                    >
                                        {content}
                                    </Alert>
                                ) : (
                                    content
                                )}
                            </Box>
                        ),
                    })
                },
                {
                    close: baseToast.close,
                    closeAll: baseToast.closeAll,
                },
            ),
        [baseToast, rtl],
    )

    return toast
}

export const Toast: React.FC<ToastProps> = () => null
